<template>
  <AppShell title="Personal">
    <template #actions>
      <div class="flex items-center gap-3">
        <p v-if="!isLoadingPersonnel" class="text-subtle">{{ personnel.length }} personer</p>
        <AppButton v-if="canManagePersonnel" color="blue" medium @click="startCreate">Lägg till personal</AppButton>
      </div>
    </template>

    <div class="space-y-4">
      <AppCard>
        <p v-if="isLoadingPersonnel">Hämtar personal...</p>
        <p v-else-if="error" class="error-text">{{ error }}</p>

        <AppTable v-else>
          <thead>
            <tr>
              <th>Namn</th>
              <th>Personnummer</th>
              <th>Roller</th>
              <th v-if="canManagePersonnel">Ändra</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="person in personnel" :key="person.id">
              <AppTableCell>
                {{ `${person.user?.givenName || ''} ${person.user?.surname || ''}`.trim() || '-' }}
              </AppTableCell>
              <AppTableCell>
                {{ person.user?.personalNumber?.toString() || '-' }}
              </AppTableCell>
              <AppTableCell>{{ formatScopes(person) }}</AppTableCell>
              <AppTableCell v-if="canManagePersonnel" action>
                <div class="row-actions">
                  <AppButton icon fab small @click="startEdit(person)">
                    <template #prepend>
                      <SvgIcon :path="mdiPencil" :size="18" />
                    </template>
                  </AppButton>
                  <AppButton
                    v-if="canRemovePersonnel(person, currentUserId)"
                    icon
                    fab
                    small
                    :aria-label="`Ta bort ${formatName(person.user) || 'personen'}`"
                    @click="startRemove(person)"
                  >
                    <template #prepend>
                      <SvgIcon :path="mdiDelete" :size="18" />
                    </template>
                  </AppButton>
                </div>
              </AppTableCell>
            </AppTableRow>

            <AppTableRow v-if="personnel.length === 0">
              <AppTableCell :colspan="canManagePersonnel ? '4' : '3'" class="text-center text-subtle">
                Ingen personal hittades.
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </AppCard>
    </div>
  </AppShell>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { mdiDelete, mdiPencil } from '@mdi/js'
import { AppButton, AppCard, AppTable, AppTableCell, AppTableRow, SvgIcon, useModal } from '@staff/ui'
import { formatName } from '@staff/shared'

import { getStoredToken, hasRequiredScope } from '@/services/auth'
import { type PersonnelListItem, type PersonnelScope } from '@/services/personnel'
import { usePersonnelDirectory } from '@/composables/usePersonnelDirectory'

import AppShell from '@/components/AppShell.vue'
import PersonnelForm from './PersonnelForm.vue'
import ConfirmRemovePersonnelModal from './ConfirmRemovePersonnelModal.vue'

const scopeLabels: Record<PersonnelScope, string> = {
  broker: 'Handläggare',
  brokerPersonnelManager: 'Personalansvarig',
  brokerPricingAdministrator: 'Prissättningsansvarig',
}

const { openModal } = useModal()
const { personnel, isLoadingPersonnel, loadPersonnelDirectory } = usePersonnelDirectory()

const error = ref('')

const canManagePersonnel = hasRequiredScope('brokerPersonnelManager')
const currentUserId = getStoredToken()?.payload?.id

// Removing oneself is rejected by the API: the remover takes over the removed person's applications.
const canRemovePersonnel = (person: PersonnelListItem, currentUserId: string | undefined) =>
  !!currentUserId && person.user?.id !== currentUserId

const translateScope = (scope: PersonnelScope) => scopeLabels[scope] || scope

const formatScopes = (person: PersonnelListItem) => {
  return person.scope.length ? person.scope.map(translateScope).join(' + ') : '-'
}

const startCreate = () => {
  openModal('Lägg till personal', PersonnelForm, {
    mode: 'create',
    onSaved: loadPersonnel,
  })
}

const startEdit = (person: PersonnelListItem) => {
  openModal('Uppdatera roller', PersonnelForm, {
    mode: 'edit',
    person,
    onSaved: loadPersonnel,
  })
}

const startRemove = (person: PersonnelListItem) => {
  openModal('Ta bort personal', ConfirmRemovePersonnelModal, {
    person,
    reloadPersonnel: loadPersonnel,
  })
}

const loadPersonnel = async () => {
  error.value = ''

  try {
    await loadPersonnelDirectory(true)
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Ett fel uppstod när personal hämtades.'
  }
}

void loadPersonnel()
</script>

<style scoped lang="scss">
.row-actions {
  display: flex;
  align-items: center;
  gap: var(--space-8);
}
</style>
